import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { Holiday, HolidayKind, holidaysApi } from '../../payroll.api';
import CrudTable from './CrudTable';

interface Draft {
  holidayDate: string;
  name: string;
  kind: HolidayKind;
  isPaid: boolean;
}

const KINDS: Record<HolidayKind, string> = { NATIONAL: 'National', FESTIVAL: 'Festival', COMPANY: 'Company', OPTIONAL: 'Optional' };

/** The year's holidays; every employee sees them under My Attendance. */
export default function HolidaysTab({ canEdit }: { canEdit: boolean }) {
  const current = new Date().getFullYear();
  const [year, setYear] = useState(current);
  return (
    <Stack gap={2}>
      <TextField select size='small' label='Year' value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 140 }}>
        {[current + 1, current, current - 1, current - 2].map((y) => (
          <MenuItem key={y} value={y}>
            {y}
          </MenuItem>
        ))}
      </TextField>
      <CrudTable<Holiday, Draft>
        entityName='Holiday'
        client={holidaysApi}
        listParams={{ year }}
        canEdit={canEdit}
        sort={(a, b) => a.holidayDate.localeCompare(b.holidayDate)}
        columns={[
          {
            label: 'Date',
            render: (h) =>
              new Date(`${h.holidayDate}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
          },
          { label: 'Holiday', render: (h) => h.name },
          { label: 'Type', render: (h) => KINDS[h.kind] ?? h.kind },
          { label: 'Paid', align: 'center', render: (h) => <Chip size='small' color={h.isPaid ? 'success' : 'default'} label={h.isPaid ? 'Paid' : 'Unpaid'} /> },
        ]}
        emptyDraft={() => ({ holidayDate: `${year}-01-01`, name: '', kind: 'FESTIVAL', isPaid: true })}
        toDraft={(h) => ({ holidayDate: h.holidayDate, name: h.name, kind: h.kind, isPaid: h.isPaid })}
        validate={(d) => (!d.holidayDate || !d.name.trim() ? 'Date and name are required.' : null)}
        toPayload={(d) => ({ holidayDate: d.holidayDate, name: d.name.trim(), kind: d.kind, isPaid: d.isPaid })}
        renderForm={(d, set) => (
          <>
            <TextField size='small' type='date' label='Date' value={d.holidayDate} onChange={(e) => set({ holidayDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField size='small' label='Name' value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder='e.g. Diwali (Lakshmi Puja)' />
            <TextField select size='small' label='Type' value={d.kind} onChange={(e) => set({ kind: e.target.value as HolidayKind })}>
              {Object.entries(KINDS).map(([k, l]) => (
                <MenuItem key={k} value={k}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel control={<Switch checked={d.isPaid} onChange={(e) => set({ isPaid: e.target.checked })} />} label='Paid holiday' />
          </>
        )}
      />
    </Stack>
  );
}
